import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import InfiniteLoader from "@/components/InfiniteLoader";
import { SectionHeading } from "@/components/Layout/Parts";
import ScrollableTable from "@/components/ScrollableTable";
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import {
  InstagramSearchImageResource,
  PaginatedResponse,
  SortState,
} from "@/types/resources";
import { api } from "@/utils/api";
import { SortDirection } from "@/utils/enums";
import ImagesTableRow from "./ImagesTableRow";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: "times_repeated",
};

const StyledTable = styled(Table)`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    font-weight: 700;
    &:nth-child(1) {
      width: 60px;
      min-width: 60px;
      padding-left: 24px;
      padding-right: 12px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  td {
    font-size: 12px;
  }
`;

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin-bottom: 0;
`;

export const CheckboxHolder = styled.div`
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 24px;
  width: 24px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purpleLighter};
  }
`;

const UploadButtonHolder = styled(motion.div)`
  position: sticky;
  top: 128px;
  display: flex;
  z-index: 2;
  justify-content: flex-end;
  transform-origin: right -97px;
`;

export const Checkbox = styled.input<{ $shouldUncheck?: boolean }>`
  display: none;
  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: "";
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${({ $shouldUncheck }) =>
        $shouldUncheck &&
        css`
          background-color: ${({ theme }) => theme.colors.white};
          border-radius: 20px;
          width: 12px;
          height: 2px;
          border: 0;
          border-width: 0;
          transform: rotate(0deg);
          top: 0;
          left: 0;
          right: 1px;
          bottom: 0;
          margin: auto;
        `}
    }
  }
`;

const ImagesTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [selectedImages, setSelectedImages] = useState<number[]>([]);

  const { id } = useParams();

  const fetchImages = ({ pageParam = "/v1/instagram-search-images" }) => {
    return api.get<never, PaginatedResponse<InstagramSearchImageResource>>(
      pageParam,
      {
        params: {
          "filter[search_id]": id,
          ...(sort.direction !== "DEFAULT" && {
            sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
          }),
        },
      }
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<InstagramSearchImageResource>>(
    ["instagram_search_images", { id, sort }],
    fetchImages,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const images = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<InstagramSearchImageResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <AnimatePresence>
        {!!selectedImages.length && (
          <UploadButtonHolder
            initial={{ scale: 0, opacity: 0.3 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0.3 }}
          ></UploadButtonHolder>
        )}
      </AnimatePresence>
      <StyledTableHeader>
        <StyledSectionHeading>Images list</StyledSectionHeading>
      </StyledTableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>
                <CheckboxHolder>
                  <Checkbox
                    id="all-images"
                    name="images"
                    type="checkbox"
                    checked={!!selectedImages.length}
                    $shouldUncheck={!!selectedImages.length}
                    onChange={() => {
                      !!selectedImages.length
                        ? setSelectedImages([])
                        : setSelectedImages(images.map((image) => image.id));
                    }}
                  />
                  <CheckboxLabel htmlFor="all-images" />
                </CheckboxHolder>
              </Th>
              <Th>Image</Th>
              <Th>Username</Th>
              <SortableTh
                property="times_repeated"
                title="Times Appeared"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="likes_count"
                title="Likes count"
                setSort={setSort}
                sort={sort}
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!images.length}
            >
              {images?.map((image) => (
                <ImagesTableRow
                  key={image.id}
                  image={image}
                  selectedImages={selectedImages}
                  setSelectedImages={setSelectedImages}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default ImagesTable;
